import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { ErrorBoundary } from '@/components/error-boundary'
import { ToastProvider } from '@/components/ui/toast'
import { Landing } from '@/routes/landing'
import { reloadForStaleChunk } from '@/lib/reload-on-stale-chunk'
import '@/styles/tokens.css'

// A tab opened against a previous build can fail to lazy-load a chunk the new
// build replaced. Vite fires `vite:preloadError`; reload to pull fresh hashes.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadForStaleChunk()
})

// Routing layout:
//   /                    → Landing (public, eager — first paint)
//   /login, /signup, …   → AuthLayout + per-page child route (lazy)
//   /admin/*             → AdminRoutes  (lazy)
//   everything else      → CustomerRoutes (lazy)
//
// Auth pages share a single lazy chunk via `auth/_tree.tsx`. After the
// first auth-page visit pulls the chunk, navigation between login /
// signup / forgot-password is instant.

// All auth modules live in one chunk — every lazy() below imports from
// the same `_tree` file, so Vite bundles them together. Each page wraps
// itself in <AuthLayout/> (props-driven, no Outlet) so there's no parent
// layout route to coordinate.
const authChunk = () => import('@/routes/auth/_tree')
const Login              = lazy(() => authChunk().then((m) => ({ default: m.Login })))
const Signup             = lazy(() => authChunk().then((m) => ({ default: m.Signup })))
const ForgotPassword     = lazy(() => authChunk().then((m) => ({ default: m.ForgotPassword })))
const ResetPassword      = lazy(() => authChunk().then((m) => ({ default: m.ResetPassword })))
const SetPassword        = lazy(() => authChunk().then((m) => ({ default: m.SetPassword })))
const VerifyEmail        = lazy(() => authChunk().then((m) => ({ default: m.VerifyEmail })))
const ResendVerification = lazy(() => authChunk().then((m) => ({ default: m.ResendVerification })))
const PendingApproval    = lazy(() => authChunk().then((m) => ({ default: m.PendingApproval })))

const CustomerRoutes = lazy(() =>
  import('@/routes/_tree').then((m) => ({ default: m.CustomerRoutes })),
)
const AdminRoutes = lazy(() =>
  import('@/routes/admin/_tree').then((m) => ({ default: m.AdminRoutes })),
)


function TreeFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-10 w-10 rounded-full border-2 border-border border-t-accent animate-spin" />
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary scope="app root">
      <ToastProvider>
      <BrowserRouter>
        <Suspense fallback={<TreeFallback />}>
          <Routes>
            {/* Public landing at /. Eager (no Suspense fallback flash). */}
            <Route
              path="/"
              element={
                <ErrorBoundary scope="landing">
                  <Landing />
                </ErrorBoundary>
              }
            />

            {/* Auth pages — each renders its own <AuthLayout/> wrapper.
                Flat registration (no parent layout route), so there's no
                descendant-Routes coupling. Matches the sister product. */}
            <Route path="/login"               element={<ErrorBoundary scope="auth tree"><Login /></ErrorBoundary>} />
            <Route path="/signup"              element={<ErrorBoundary scope="auth tree"><Signup /></ErrorBoundary>} />
            <Route path="/forgot-password"     element={<ErrorBoundary scope="auth tree"><ForgotPassword /></ErrorBoundary>} />
            <Route path="/reset-password"      element={<ErrorBoundary scope="auth tree"><ResetPassword /></ErrorBoundary>} />
            <Route path="/set-password"        element={<ErrorBoundary scope="auth tree"><SetPassword /></ErrorBoundary>} />
            <Route path="/verify-email"        element={<ErrorBoundary scope="auth tree"><VerifyEmail /></ErrorBoundary>} />
            <Route path="/resend-verification" element={<ErrorBoundary scope="auth tree"><ResendVerification /></ErrorBoundary>} />
            <Route path="/pending-approval"    element={<ErrorBoundary scope="auth tree"><PendingApproval /></ErrorBoundary>} />

            {/* Admin tree — owns /admin/* including /admin/login */}
            <Route
              path="/admin/*"
              element={
                <ErrorBoundary scope="admin tree">
                  <AdminRoutes />
                </ErrorBoundary>
              }
            />

            {/* Customer tree — everything else under the customer Layout */}
            <Route
              path="/*"
              element={
                <ErrorBoundary scope="customer tree">
                  <CustomerRoutes />
                </ErrorBoundary>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  </StrictMode>,
)
